import { CheckboxField } from '@oberoninternal/travelbase-ds/components/form/Checkbox';
import SelectInput from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { TextInputField } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Label } from '@oberoninternal/travelbase-ds/components/primitive/Label';
import { Box, Flex } from '@rebass/grid';
import { useFormikContext, setNestedObjectValues } from 'formik';
import { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useTenantContext } from '../context/TenantContext';
import { useCountryOptions } from '../hooks/useDetailsFormOptions';
import { DetailsValues } from '../utils/getInitialCustomerInfoValues';
import BirthdateField from './designsystem/BirthdateField';
import Fieldset, { FormBox } from './designsystem/Fieldset';
import Caret from './svg/Caret';
import { getFontCss } from '@oberoninternal/travelbase-ds/constants/theme';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';

interface Props {
    hidePersonals?: boolean;
    /**
     * this prop can be used in order to show invalid fields initially
     */
    touchErrorFields?: boolean;
    disableEmailField?: boolean;
}

const SettingsDetailsForm: FC<Props> = ({ hidePersonals, touchErrorFields, disableEmailField = false }) => {
    const { brandConfig } = useTenantContext();
    const { initialValues, setTouched, errors } = useFormikContext<DetailsValues>();
    const countryOptions = useCountryOptions();

    // used in order to show invalid fields initially. In the modal for example.
    useEffect(() => {
        if (touchErrorFields && Object.keys(errors).length > 0) {
            setTouched(setNestedObjectValues(errors, true));
        }
    }, [errors, setTouched, touchErrorFields]);
    return (
        <>
            {!hidePersonals && (
                <SectionWrapper>
                    <SectionTitle>
                        <FormattedMessage defaultMessage="Persoonsgegevens" />
                    </SectionTitle>
                    <StyledFieldSet
                        label={
                            <StyledLabel>
                                <FormattedMessage defaultMessage="Voornaam" />
                            </StyledLabel>
                        }
                        id="firstName"
                        dataCy="detailsFormFirstName"
                        w="30"
                        mw="80"
                    >
                        <StyledTextInputField name="customerInfo.firstName" id="firstName" data-cy="firstName" />
                    </StyledFieldSet>

                    <StyledFieldSet
                        label={
                            <StyledLabel>
                                <FormattedMessage defaultMessage="Achternaam" />
                            </StyledLabel>
                        }
                        id="lastName"
                        dataCy="detailsFormLastName"
                        w="30"
                        mw="80"
                    >
                        <StyledTextInputField name="customerInfo.lastName" id="lastName" data-cy="lastName" />
                    </StyledFieldSet>

                    <StyledFieldSet
                        label={
                            <StyledLabel>
                                <FormattedMessage defaultMessage="Geboortedatum" />
                            </StyledLabel>
                        }
                        id="birthdate.day"
                        dataCy="detailsFormBirthDate"
                    >
                        <BirthdateWrapper>
                            <BirthdateField name="birthdate" />
                        </BirthdateWrapper>
                    </StyledFieldSet>
                </SectionWrapper>
            )}
            {!hidePersonals && (
                <SectionWrapper>
                    <SectionTitle>
                        <FormattedMessage defaultMessage="Contactgegevens" />
                    </SectionTitle>
                    <StyledFieldSet
                        label={
                            <StyledLabel>
                                <FormattedMessage defaultMessage="E-mailadres" />
                            </StyledLabel>
                        }
                        id="email"
                        dataCy="detailsFormEmail"
                        w="50"
                        mw="100"
                    >
                        <StyledTextInputField
                            name="customerInfo.email"
                            type="email"
                            id="email"
                            data-cy="email"
                            disabled={disableEmailField}
                        />
                    </StyledFieldSet>

                    <StyledFieldSet
                        label={
                            <StyledLabel>
                                <FormattedMessage defaultMessage="Telefoonnummer" />
                            </StyledLabel>
                        }
                        id="phoneNumber"
                        dataCy="detailsFormPhoneNumber"
                        w="30"
                        mw="100"
                    >
                        <StyledTextInputField
                            name="customerInfo.phoneNumber"
                            type="tel"
                            id="phoneNumber"
                            data-cy="phoneNumber"
                        />
                    </StyledFieldSet>
                </SectionWrapper>
            )}
            <SectionWrapper>
                <SectionTitle>
                    <FormattedMessage defaultMessage="Adresgegevens" />
                </SectionTitle>
                <StyledFieldSet
                    label={
                        <StyledLabel>
                            <FormattedMessage defaultMessage="Straatnaam" />
                        </StyledLabel>
                    }
                    id="street"
                    dataCy="detailsFormStreet"
                    w="50"
                    mw="100"
                >
                    <StyledTextInputField name="customerInfo.address.street" id="street" data-cy="street" />
                </StyledFieldSet>

                <StyledFieldSet
                    label={
                        <StyledLabel>
                            <FormattedMessage defaultMessage="Huisnummer" />
                        </StyledLabel>
                    }
                    id="houseNumber"
                    dataCy="detailsFormHouseNumber"
                    mw="50"
                >
                    <Box width={[1, null, '20.7rem']}>
                        <StyledTextInputField
                            name="customerInfo.address.number"
                            id="houseNumber"
                            data-cy="houseNumber"
                            maxLength={20}
                        />
                    </Box>
                </StyledFieldSet>

                <StyledFieldSet
                    label={
                        <StyledLabel>
                            <FormattedMessage defaultMessage="Postcode" />
                        </StyledLabel>
                    }
                    id="postalCode"
                    dataCy="detailsFormPostalCode"
                    mw="50"
                >
                    <Box width={[1, null, '20.7rem']}>
                        <StyledTextInputField
                            name="customerInfo.address.postalCode"
                            id="postalCode"
                            data-cy="postalCode"
                            maxLength={20}
                        />
                    </Box>
                </StyledFieldSet>

                <StyledFieldSet
                    label={
                        <StyledLabel>
                            <FormattedMessage defaultMessage="Woonplaats" />
                        </StyledLabel>
                    }
                    id="city"
                    dataCy="detailsFormCity"
                    w="30"
                    mw="100"
                >
                    <StyledTextInputField name="customerInfo.address.city" id="city" data-cy="city" />
                </StyledFieldSet>

                <StyledFieldSet
                    label={
                        <StyledLabel>
                            <FormattedMessage defaultMessage="Land" />
                        </StyledLabel>
                    }
                    id="country"
                    dataCy="detailsFormCountry"
                >
                    <Box width={[1, null, '20.7rem']}>
                        <SelectInput
                            name="customerInfo.address.countryCode"
                            hideChoices
                            ToggleComponent={Caret}
                            options={countryOptions}
                            filterFromStart
                            id="country"
                            isSearchable
                        />
                    </Box>
                </StyledFieldSet>
            </SectionWrapper>
            {!brandConfig.disableNewsLetter && 'newsletterOptin' in initialValues.customerInfo && (
                <NewsLetter>
                    <FormBox>
                        <Flex>
                            <CheckboxField
                                name="customerInfo.newsletterOptin"
                                id="newsletterOptin"
                                data-cy="newsletterOptin"
                            />
                            <Box>
                                <Label htmlFor="newsletterOptin">
                                    <Body>
                                        <FormattedMessage defaultMessage="Ik ontvang graag de nieuwsbrief van VVV Texel" />
                                    </Body>
                                </Label>
                            </Box>
                        </Flex>
                    </FormBox>
                </NewsLetter>
            )}
        </>
    );
};

export default SettingsDetailsForm;

const NewsLetter = styled.div`
    margin-top: ${({ theme }) => theme.spacing['50_Semi']};
`;

const StyledTextInputField = styled(TextInputField)`
    min-height: ${({ theme }) => theme.spacing['70_XLarge']};
`;

const StyledFieldSet = styled(Fieldset)<{ w?: string; mw?: string }>`
    width: ${({ w }) => `${w}%`};

    @media (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: ${({ mw }) => `${mw}%`};
    }
`;

const SectionTitle = styled(Title)`
    ${({ theme }) => getFontCss(theme.fontSizes.title.regular)};
    margin-bottom: 2.4rem;
`;

const BirthdateWrapper = styled.div`
    width: 50%;

    div:first-child {
        max-width: 100%;
    }

    @media (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 100%;
    }
`;

const SectionWrapper = styled.div`
    margin-bottom: 4.8rem;
`;

const StyledLabel = styled(Body)`
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
    color: ${({ theme }) => theme.colors.primary[80]};
`;
